import { useFormContext } from 'react-hook-form';

import useFleetAdminPermissions from 'utils/User/useFleetAdminPermissions';

interface UseCreateDepotCapabilityArgs {
  /**
   * Provide directly when the modal is opened with a fixed Platform
   * Account in scope (e.g. from the Account detail page). When omitted,
   * the account id is read from the in-modal Account dropdown's
   * selection (the `accountId` form field).
   */
  platformAccountId?: string;
}

interface UseCreateDepotCapabilityResult {
  selectedAccountId?: string;
  /**
   * `null` when no account can be resolved yet (e.g. the user hasn't
   * picked an account and no override was supplied).
   */
  capability: FleetAdminCapability | null;
}

/**
 * Resolve the Fleet Admin "canManageDepots" capability for whatever
 * Platform Account the CreateDepotModal currently points at. Pure
 * compute over its inputs — no Apollo subscriptions of its own.
 */
export default function useCreateDepotCapability({
  platformAccountId,
}: UseCreateDepotCapabilityArgs): UseCreateDepotCapabilityResult {
  const { watch } = useFormContext();
  const fleetAdminPermissions = useFleetAdminPermissions();

  const formAccountId = watch('accountId');
  const selectedAccountId = platformAccountId || formAccountId || undefined;

  if (!selectedAccountId) {
    return { selectedAccountId: undefined, capability: null };
  }

  return {
    selectedAccountId,
    capability: fleetAdminPermissions.accountCapability(
      selectedAccountId,
      'canManageDepots',
    ),
  };
}
